// Types
import {
  ActivityType,
  Certification,
  Education,
  ActionGroup,
  ProjectStatus,
  Skill,
  ProjectActivityData,
  WorkPlace,
  Division,
} from '@shared/types';

// Constants
import {
  TimeOffResponseStatus,
  EmployeeTrainingStatus,
} from '@/constants/employee';
import { UserStatus } from '@shared/constants';

import { PROJECT_DATE_STATUS } from './project';
import { MetaData } from './api';

export type YearReviews = string[];

export interface EmployeeInfo {
  id: string;
  division?: Division;
  status?: UserStatus;
  workingStatus?: string;
  address?: string;
  name: string;
  email: string;
  title: string;
  bio?: string;
  avatar?: string;
  gender?: string;
  phone?: string;
  username?: string;
  birthday?: string;
  room?: string;
  onboardedAt?: string;
}

export interface TrainingInteractions {
  name: string;
  author: string;
  title?: string;
  avatar?: string;
  feedback: string;
  createdAt?: string;
}

export interface TrainingHistory {
  type: EmployeeTrainingStatus;
  time: string;
}

export type EmployeeSupporter = Pick<
  EmployeeInfo,
  'name' | 'avatar' | 'email' | 'username'
>;

export interface TrainingParticipant {
  email?: string;
  username?: string;
  name?: string;
  avatar?: string;
  presentations?: string[];
}

export interface EmployeeTraining {
  id: string;
  user: string;
  topic: string;
  topicLabel?: string;
  status: EmployeeTrainingStatus;
  archived?: boolean;
  tag?: string;
  desc?: string | null;
  supporters?: EmployeeSupporter[];
  start?: string;
  estEnd?: string;
  actualEnd?: string | null;
  histories?: TrainingHistory[];
  activities: (Omit<EmployeeActivity, 'data'> & {
    data: EmployeeTrainingActivity;
  })[];
  participants?: TrainingParticipant[];
}

export interface ProjectActivityDate {
  time: Date;
  type: PROJECT_DATE_STATUS;
}

export interface EmployeeProjectResponse {
  data: EmployeeProject[];
  meta: MetaData;
}

export interface EmployeeProject {
  name: string;
  code: string;
  role: string;
  logo?: string | null;
  desc?: string;
  status: ProjectStatus;
  archived?: boolean;
  histories: ProjectActivityDate[];
  userRoleColor?: string;
  kickOffDate?: string;
  tag?: string | null;
}

export interface EmployeeLunchRegistration {
  office?: WorkPlace;
  lunch?: boolean;
  vegetarian?: boolean;
  breakfast?: boolean;
  id?: string;
}

export type EmployeeEducation = Omit<Education, 'admission' | 'graduation'> & {
  admission?: string;
  graduation?: string;
};

export enum EmployeeSupporterStatus {
  SUPPORTING = 'supporting',
  UN_SUPPORTED = 'unsupported',
}
export interface EmployeeProfile {
  name: string;
  email: string;
  username: string;
  title?: string;
  avatar?: string;
  topics?: {
    topic: string;
    topicLabel: string;
    status?: EmployeeTrainingStatus;
    supporterStatus?: EmployeeSupporterStatus;
    archived?: boolean;
    trainingId?: string;
  }[];
  bio?: string;
}

export interface EmployeeProfileWebsocketResponse extends EmployeeProfile {
  user: string;
  related: string;
  relatedName: string;
  relatedUsername: string;
  relatedAvatar?: string;
  training?: EmployeeTraining;
  id: string;
  supporters?: EmployeeProfile[];
  topicLabel?: string;
  activities: (Omit<EmployeeActivity, 'data'> & {
    data: EmployeeTrainingActivity;
  })[];
}

export interface EmployeeRelationship {
  mentor?: EmployeeProfile;
  mentees: EmployeeProfile[];
  supportees: EmployeeProfile[];
  supporters: EmployeeProfile[];
}

export interface TimeOffRecipient {
  user: string;
  name: string;
  avatarUrl: string;
  status: TimeOffResponseStatus;
  actionAt?: string | null;
}

export type EmployeeSkill = Skill;
export type EmployeeCertification = Certification;

export interface Employee {
  info?: EmployeeInfo;
  relationship?: EmployeeRelationship;
  trainings?: EmployeeTraining[];
  projects?: EmployeeProject[];
  skills?: EmployeeSkill[];
  certs?: EmployeeCertification[];
  education?: EmployeeEducation[];
  timeOffRequests?: number;
  activities?: EmployeeActivity[];
  yearReviews?: YearReviews;
}

// Base interface for employee request data
export interface BaseEmployeeRequestData {
  user: string; // Email of the employee
  name: string;
  note: string;
  for?: string;
}

export interface EmployeeRequestReallocationData
  extends BaseEmployeeRequestData {
  origin: string;
  dest: string;
  time: string;
}

export interface EmployeeRequestRoomBookingData
  extends BaseEmployeeRequestData {
  room: string;
  start: string;
  end: string;
}

export interface EmployeeRequestTimeOffData extends BaseEmployeeRequestData {
  start: string;
  end: string;
}

export interface EmployeeRequestMaintenanceData
  extends BaseEmployeeRequestData {
  scope: string;
}

export interface EmployeeRequestLunchRegistrationData
  extends BaseEmployeeRequestData {
  office: WorkPlace;
  lunch: boolean;
  vegetarian: boolean;
}

// User relationship activity data
export interface EmployeeUserRelationshipData {
  relatedName?: string; // Name of the related user
  related?: string; // Email of the related user
  relatedAvatar?: string; // Avatar of the related user
  name?: string; // Name of the user being viewed
  user: string; // Email of the user being viewed
  avatar?: string; // Avatar of the user being viewed
  training?: string; // Training topic
}

export type EmployeeTrainingActivity = Pick<
  EmployeeTraining,
  'user' | 'topic'
> & {
  id: string;
  name: string;
  training: string;
  archived?: boolean;
  feedback?: string;
  related?: string;
  relatedName?: string;
  relatedAvatar?: string;
  relatedTitle?: string;
  presentationAt?: Date;
  presentationContent?: string;
  presentationTitle?: string;
  presentationId?: string;
  occurredAt?: string;
  recipients?: EmployeeProfile[];
  feedbackSentiment?: string;
  feedbackId?: string;
  authorName?: string;
  authorAvatar?: string;
  topicLabel?: string;
};

export type EmployeeActivityData =
  | EmployeeTrainingActivity
  | EmployeeRequestReallocationData
  | EmployeeRequestRoomBookingData
  | EmployeeRequestTimeOffData
  | EmployeeRequestMaintenanceData
  | EmployeeRequestLunchRegistrationData
  | EmployeeUserRelationshipData;

export interface EmployeeActivity {
  id: string;
  type: ActivityType;
  group: ActionGroup;
  by?: string;
  data?: ProjectActivityData | EmployeeActivityData | EmployeeTrainingActivity;
  occurredAt: string;
}

export interface EmployeeActivitiesResponse {
  data: EmployeeActivity[];
  meta: MetaData;
}

export interface TrainingTimelineActivity
  extends Omit<EmployeeActivity, 'data'> {
  data: EmployeeTrainingActivity;
  supporters?: TrainingTimelineActivity[];
}

export type PresentationActivity = Omit<EmployeeActivity, 'data'> & {
  data: EmployeeTrainingActivity;
};

export type RequestFeedbackPresentation = {
  activityId: string;
  participants: string[];
};

export type RequestFeedbackPayload = {
  identity: string;
  type: ActivityType;
  data: RequestFeedbackPresentation;
};

export type RevokeRequestFeedbackPayload = {
  identity: string;
  type: ActivityType;
  data: {
    activityId: string;
    participants: string[];
  };
};
